import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  InvisibleUnicodeDemo,
  MaliciousExtensionDemo,
  BlockedMcpServerDemo,
  SecretDetectionDemo,
  ChatSecurityDemo,
} from './attack-demonstrations';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';

interface AttackSection {
  title: string;
  content: React.ReactNode;
}

const attacks: AttackSection[] = [
  {
    title: 'Invisible Unicode in Rules',
    content: <InvisibleUnicodeDemo />,
  },
  {
    title: 'Malicious Extension',
    content: <MaliciousExtensionDemo />,
  },
  {
    title: 'MCP Server Package Impersonation Attack',
    content: <BlockedMcpServerDemo />,
  },
  {
    title: 'Secret Detection',
    content: <SecretDetectionDemo />,
  },
  {
    title: 'Chat Security',
    content: <ChatSecurityDemo />,
  },
];

/**
 * Attack Demonstrations tab component that can be used in both
 * dashboard and organization views
 */
export default function AttackDemonstrationsTab() {
  return (
    <div className="space-y-6">
      <Card className="gap-0">
        <CardHeader>
          <CardTitle className="text-md">
            <Info className="w-5 mr-2 inline" />
            How to use Attack Demonstrations
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground ml-9">
          Use the demos below to safely simulate attack scenarios and review how
          Kirin detects them. To reduce alert fatigue in the extension and
          agent, Kirin may group repeated notifications by severity. Some demo
          notifications may not appear immediately and may be shown later as a
          summary. You can immediately view all detection results in the{' '}
          <Link
            to="../alerts"
            className="text-foreground underline underline-offset-2 hover:opacity-80"
          >
            Alerts page
          </Link>
          .
        </CardContent>
      </Card>
      {attacks.map((attack, index) => (
        <Card key={index}>
          <details className="group">
            <summary className="cursor-pointer list-none">
              <CardHeader className="hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <span className="text-muted-foreground group-open:rotate-90 transition-transform">
                    ▶
                  </span>
                  {attack.title}
                </CardTitle>
              </CardHeader>
            </summary>
            <CardContent className="pt-0">{attack.content}</CardContent>
          </details>
        </Card>
      ))}
    </div>
  );
}
